import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Save, RefreshCw, TrendingUp, Plus, Trash2, GripVertical } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

const CMSKeyStatsPage = () => {
  const [keyStats, setKeyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const statsRes = await api.get('/settings/key-stats');
      
      setKeyStats(statsRes.data?.stats || [
        { id: '1', value: 15, suffix: '+', label: "Années d'expérience" },
        { id: '2', value: 200, suffix: '+', label: "Clients accompagnés" },
        { id: '3', value: 500, suffix: '+', label: "Créations réalisées" },
        { id: '4', value: 6, suffix: '', label: "Experts passionnés" }
      ]);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStats = async () => {
    setSaving(true);
    try {
      await api.put('/settings/key-stats', { stats: keyStats });
      toast.success('Chiffres clés mis à jour !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateStat = (index, field, value) => {
    const updated = [...keyStats];
    updated[index] = { ...updated[index], [field]: value };
    setKeyStats(updated);
  };

  const addStat = () => {
    setKeyStats([...keyStats, { 
      id: Date.now().toString(), 
      value: 0, 
      suffix: '+', 
      label: 'Nouveau chiffre' 
    }]);
  };

  const removeStat = (index) => {
    setKeyStats(keyStats.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Chiffres clés</h1>
        <p className="text-slate-500">Gérez les statistiques affichées sur la page d'accueil</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-500" />
            Chiffres clés de la page d'accueil
          </CardTitle>
          <CardDescription>
            Ces chiffres s'affichent dans la section statistiques du site (ex: 15+ Années d'expérience)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {keyStats.map((stat, index) => (
            <div key={stat.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
              <GripVertical className="w-5 h-5 text-slate-400 cursor-move" />
              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Valeur</Label>
                  <Input
                    type="number"
                    value={stat.value}
                    onChange={(e) => updateStat(index, 'value', parseInt(e.target.value) || 0)}
                    placeholder="15"
                  />
                </div>
                <div>
                  <Label>Suffixe</Label>
                  <Input
                    value={stat.suffix}
                    onChange={(e) => updateStat(index, 'suffix', e.target.value)}
                    placeholder="+ ou %"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Libellé</Label>
                  <Input
                    value={stat.label}
                    onChange={(e) => updateStat(index, 'label', e.target.value)}
                    placeholder="Années d'expérience"
                  />
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => removeStat(index)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}

          <Button variant="outline" onClick={addStat} className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un chiffre
          </Button>

          <div className="flex justify-end pt-4">
            <Button onClick={handleSaveStats} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-600">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer les chiffres'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CMSKeyStatsPage;
